import * as types from '../mutation-types';
import HubAPI from '../../api/hub';
import AgentAPI from '../../api/agents';

const defaultAgentFlags = { isFetching: false, fetchFailed: false };

let tenantFetchPromise = null;

export const state = {
  projects: [],
  workspaces: [],
  hubUserData: {},
  hubUserDataFlags: {},
  isHubAdmin: false,
  uiFlags: {
    isFetchingTenant: false,
    fetchTenantFailed: false,
  },
};

export const getters = {
  getProjects: $state => $state.projects,
  getWorkspaces: $state => $state.workspaces,
  getHubUserData: $state => agentId => $state.hubUserData[agentId] || {},
  getHubUserDataFlags: $state => agentId =>
    $state.hubUserDataFlags[agentId] || { ...defaultAgentFlags },
  getIsHubAdmin: $state => $state.isHubAdmin,
  getUIFlags: $state => $state.uiFlags,
};

export const actions = {
  fetchTenant: ({ commit }) => {
    // Dispatched from several mount points (Sidebar, ChannelList, Settings,
    // route guards); share the in-flight request so concurrent calls dedupe
    // without resolving early — route guards await the dispatch and then read
    // getIsHubAdmin, so every caller must observe the fetch completing.
    if (tenantFetchPromise) return tenantFetchPromise;
    tenantFetchPromise = (async () => {
      commit(types.default.SET_HUB_TENANT_FETCHING_STATUS, true);
      commit(types.default.SET_HUB_TENANT_FETCH_FAILED, false);
      try {
        const { data } = await HubAPI.getTenant();
        commit(types.default.SET_HUB_PROJECTS, data.claudia_projects || []);
        commit(types.default.SET_HUB_WORKSPACES, data.eddie_workspaces || []);
        commit(types.default.SET_HUB_IS_HUB_ADMIN, Boolean(data.isHubAdmin));
      } catch (error) {
        commit(types.default.SET_HUB_TENANT_FETCH_FAILED, true);
      } finally {
        commit(types.default.SET_HUB_TENANT_FETCHING_STATUS, false);
        tenantFetchPromise = null;
      }
    })();
    return tenantFetchPromise;
  },
  fetchHubUserData: async ({ commit }, agentId) => {
    commit(types.default.SET_HUB_USER_DATA_FETCHING_STATUS, {
      agentId,
      status: true,
    });
    commit(types.default.SET_HUB_USER_DATA_FETCH_FAILED, {
      agentId,
      status: false,
    });
    try {
      const { data } = await AgentAPI.hubUserData(agentId);
      commit(types.default.SET_HUB_USER_DATA, { agentId, data });
    } catch (error) {
      commit(types.default.SET_HUB_USER_DATA_FETCH_FAILED, {
        agentId,
        status: true,
      });
    } finally {
      commit(types.default.SET_HUB_USER_DATA_FETCHING_STATUS, {
        agentId,
        status: false,
      });
    }
  },
};

export const mutations = {
  [types.default.SET_HUB_TENANT_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetchingTenant = status;
  },
  [types.default.SET_HUB_USER_DATA_FETCHING_STATUS](
    $state,
    { agentId, status }
  ) {
    const current = $state.hubUserDataFlags[agentId] || {
      ...defaultAgentFlags,
    };
    $state.hubUserDataFlags = {
      ...$state.hubUserDataFlags,
      [agentId]: { ...current, isFetching: status },
    };
  },
  [types.default.SET_HUB_PROJECTS]($state, data) {
    $state.projects = data;
  },
  [types.default.SET_HUB_WORKSPACES]($state, data) {
    $state.workspaces = data;
  },
  [types.default.SET_HUB_IS_HUB_ADMIN]($state, value) {
    $state.isHubAdmin = value;
  },
  [types.default.SET_HUB_USER_DATA]($state, { agentId, data }) {
    $state.hubUserData = { ...$state.hubUserData, [agentId]: data };
  },
  [types.default.SET_HUB_TENANT_FETCH_FAILED]($state, status) {
    $state.uiFlags.fetchTenantFailed = status;
  },
  [types.default.SET_HUB_USER_DATA_FETCH_FAILED]($state, { agentId, status }) {
    const current = $state.hubUserDataFlags[agentId] || {
      ...defaultAgentFlags,
    };
    $state.hubUserDataFlags = {
      ...$state.hubUserDataFlags,
      [agentId]: { ...current, fetchFailed: status },
    };
  },
};

export default { namespaced: true, state, getters, actions, mutations };
